// ============================================
// APPLICATION - Categorías Almacén Hooks
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriasAlmacenRepository } from '../../infrastructure/http/categorias-almacen.repository';
import { queryKeys } from '../query-keys';
import { useAuthStore } from '../../state/auth.store';

const useIsEnabled = () => {
  const { accessToken, session } = useAuthStore();
  return !!(accessToken && session?.hasPremiumAccess && session?.user?.esPropietario);
};

export const useCategoriasAlmacen = () => {
  const isEnabled = useIsEnabled();

  return useQuery({
    queryKey: queryKeys.categoriasAlmacen.list(),
    queryFn: () => categoriasAlmacenRepository.getAll(),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    throwOnError: false,
  });
};

export const useCreateCategoriaAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (nombre: string) => categoriasAlmacenRepository.create(nombre),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categoriasAlmacen.list() });
    },
  });
};

export const useUpdateCategoriaAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, nombre }: { id: number; nombre: string }) =>
      categoriasAlmacenRepository.update(id, nombre),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categoriasAlmacen.list() });
    },
  });
};

export const useDeleteCategoriaAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => categoriasAlmacenRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categoriasAlmacen.list() });
    },
  });
};

export const useAssignCategoriaAlmacen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productoAlmacenId,
      categoriaAlmacenId,
    }: {
      productoAlmacenId: number;
      categoriaAlmacenId: number;
    }) => categoriasAlmacenRepository.assignToProduct(productoAlmacenId, categoriaAlmacenId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', variables.productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};

export const useRemoveCategoriaFromProducto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productoAlmacenId: number) =>
      categoriasAlmacenRepository.removeFromProduct(productoAlmacenId),
    onSuccess: (_data, productoAlmacenId) => {
      queryClient.invalidateQueries({ queryKey: ['producto', 'vendible', productoAlmacenId] });
      queryClient.invalidateQueries({ queryKey: ['productos', 'sellable'] });
    },
  });
};

